import { Localization, SessionContext, SettingsManager } from "@viamap/viamap2-common";
import { useContext, useEffect, useMemo, useRef } from "react";
import { ADModalBodyNoScroll, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { ViewButtonCheck, ViewButtonCollapser, ViewButtonFlex, ViewButtonGroup, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { MapitStateContext, SetPrintLayers, MapInteractionState, actionSetMapInteractionState } from "src/states/MapitState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, WindowId, openWindow } from "src/WindowManager/WindowState";
import { bbox, booleanContains, booleanIntersects, featureCollection, polygon, truncate } from "@turf/turf";
import polylabel from "polylabel";
import { GeoJSONSource } from "maplibre-gl";
import { addElement, FRPContext, FRPElement, FRPMessage, FRPNotificationContext, FRPState, getMyAccessRights, loadProjectState, selectProject, setShownElementsIds, setSelectedElementIds, setView, setFilter, setVisualizationId } from "./FRPState";
import { actionAddAvailableFeature, actionRemoveAvailableFeature, ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { message } from "src/components/Message";
import { FRPConst } from "./FRPConst";
import { FeaturesAccessibleFRPUserAccessRights } from "../FRPModule/FRPState";
import { FRPElementEditor } from "./FRPElementEditor";
import { FRPContentView } from "./FRPContentView";
import { FRPProjektView } from "./FRPProjektView";
import { FRPFunc } from "./FRPFunc";
import chroma from "chroma-js";
import { MitWebsocket, ServerMessages } from "src/common/managers/MitWebsocket";
import { MitLayerHandleMaplibre } from "src/managers/MapFacadeMaplibre";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { parse } from "wkt";
import { FRPSearchBar } from "./FRPSearchBar";
import { FRPProjectSelect } from "./FRPProjectSelect";
import { useMitStorage } from "src/managers/UseMitStorage";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { areaByUserPreference } from "src/managers/PlanarCalculationMethods";




const layerName = "FRP_Layer";



export function FRPAnotationsDialog(props: {
   managedWindow: any;
}) {

   const { state: sessionState } = useContext(SessionContext);
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const zoomed = useRef<string | undefined>(undefined);
   const { hasAccessToFeature, dispatch: applicationStateDispatch, state: applicationState } = useContext(ApplicationStateContext);
   const hovered = useRef<string[] | null>(null);

   const { state: frpState, dispatch: frpDispatch } = useContext(FRPContext);
   const { state: notification } = useContext(FRPNotificationContext);
   const [autoFit, setAutoFit] = useMitStorage<boolean>("frpAutoFit", true);

   useEffect(() => {
      mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Override));

      return () => {
         mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Normal));
      };
   }, []);

   useEffect(() => {
      if (frpState.selectedProject && frpState.selectedProject.esIndexName) {
         let projectEsID = frpState.selectedProject.esIndexName;
         let userName = sessionState.userName;

         let x = frpState.selectedProject.id
         let flag_alreadyWaiting = false
         let flag_abortcontroller = new AbortController();
         frpDispatch(loadProjectState(projectEsID, userName));
         let callBack = (e: ServerMessages) => {
            if (flag_alreadyWaiting) return;
            if (e.type === "update") {
               flag_alreadyWaiting = true
               setTimeout(() => {
                  flag_alreadyWaiting = false
                  if (flag_abortcontroller.signal.aborted) return;
                  setTimeout(() => {
                     if (flag_abortcontroller.signal.aborted) return;
                     frpDispatch(loadProjectState(projectEsID, userName));
                  }, 5000) // Assumes 5 seconds to ingest elastic changes
               }, 10000)
            }
         }
         MitWebsocket.subscribe(x, callBack);
         return () => {
            flag_abortcontroller.abort();
            MitWebsocket.unsubscribe(x, callBack);
         }
      }
      // Update access rights
   }, [frpState.selectedProject]);

   useEffect(() => {
      if (frpState.selectedProject) {
         SettingsManager.clearByPriority(FRPConst.settingsFixedPriorities);
         SettingsManager.addSettings(frpState.selectedProject.settings, { filename: "LiveSetting", priority: FRPConst.settingsFixedPriorities });
         let accessRights = getMyAccessRights(frpState.selectedProject, sessionState.userRef, sessionState.customerRef);
         // Mapping of BNBO oriented access rights to the general access rights
         if (accessRights && accessRights.length == 1) {
            // For now only a single access right is supported
            // let myAllowDeny = FeaturesAccessibleBNBOUserAccessRights[accessRights[0]];
            // if (!myAllowDeny) {
            // appMessageDispatch(actionSetErrorMessage("No access rights found for this project"));
            // return;
            // }
            let myAllowDeny = FeaturesAccessibleFRPUserAccessRights[accessRights[0]];
            for (let feature of myAllowDeny.allow) {
               applicationStateDispatch(actionAddAvailableFeature(feature));
            }
            for (let feature of myAllowDeny.deny) {
               applicationStateDispatch(actionRemoveAvailableFeature(feature));
            }
         } else {
            message.error("No access rights found for this project");
         }
      }
   }, [frpState.selectedProject]);




   const frpMapFeatures = useMemo(() => {

      const filter = FRPFunc.frpHandleFilter(frpState.filter, frpState);
      let filteredElements = Object.values(frpState.elements).filter(filter);

      const filterViewContext = (element: FRPElement) => {
         if (frpState.shownElementsIds?.length === 0) {
            return FRPFunc.determineAllRootElements(frpState).includes(element.id);
         }
         return frpState.shownElementsIds?.includes(element.id);
      };

      filteredElements = filteredElements.filter(filterViewContext);




      let x = filteredElements.map((item) => {
         let expectedStartDate = FRPFunc.determineParentAttribute(item, frpState, "expectedStartDate");
         let expectedEndDate = FRPFunc.determineParentAttribute(item, frpState, "expectedEndDate");
         let status = FRPFunc.determineParentAttribute(item, frpState, "status");

         // Enhanced unread logic - check element AND descendants
         const hasUnread =
            (frpState.selectedProjectId && notification[frpState.selectedProjectId]?.[item.id] < new Date(item.lastChangedTimestamp) && item.lastChangedUser !== sessionState.userRef) ||
            frpState.messages.filter((a) => a.elementId == item.id && (notification[frpState.selectedProjectId!]?.[item.id] || new Date()) < new Date(a.edited || a.created) && a.user !== sessionState.userRef).length > 0 ||
            // Check descendants
            FRPFunc.getAllDescendants(item.id, frpState.childrenLookupTable).some(descendantId => {
               const descendant = frpState.elements[descendantId];
               return descendant && (
                  (notification[frpState.selectedProjectId!]?.[descendantId] < new Date(descendant.lastChangedTimestamp) && descendant.lastChangedUser !== sessionState.userRef) ||
                  frpState.messages.filter((msg) =>
                     msg.elementId == descendantId && (notification[frpState.selectedProjectId!]?.[descendantId] || new Date()) < new Date(msg.edited || msg.created) && msg.user !== sessionState.userRef).length > 0
               );
            });

         return {
            type: "Feature",
            properties: {
               ...item,
               ueid: item.id,
               expectedStartDate: expectedStartDate?.value,
               expectedEndDate: expectedEndDate?.value,
               status: status?.value,
               unread: Number(hasUnread),
            },
            geometry: item.geometry,
         };
      })
         .filter((item) => item.geometry)
         .filter((item) => item.properties.ueid);
      if (x.length !== filteredElements.length) {
         message.error("Some FRP elements are missing geometry or id, please check your data.");
      }

      return x.map((item) => { return truncate(item as any, { precision: 6 }) as any; }) || [];


   }, [frpState.elements, notification, frpState.filter, frpState.view, frpState.shownElementsIds, frpState.selectedElementIds, frpState.selectedProjectId, frpState.messages]);

   const pointAsFeatures = useMemo(() => {
      return frpMapFeatures
         .filter(item => "geopoint" in item.properties && item.properties.geopoint && item.properties.geopoint !== "")
         .map(item => ({
            ...item,
            geometry: parse(item.properties.geopoint) as unknown as { type: "Point", coordinates: number[] }
         }))
         .filter(item => item.properties.unread);
   }, [frpMapFeatures]);

   // Convert polygon/multipolygon features to point features for text placement
   const textPointFeatures = useMemo(() => {
      let colorReadyLayer = modLayerForVisualization(frpMapFeatures, frpState.visualizationId, frpState);
      let textReadyLayer = modLayerTextForVisualization(colorReadyLayer, frpState.visualizationId, frpState);

      return textReadyLayer
         .filter((item: any) => item.geometry && item.properties?.displayText)
         .map((item: any) => {
            const geom = item.geometry;
            let pointCoords: number[] | null = null;

            let calculatedArea = 0;

            if (geom.type === "Polygon") {
               const poly = polygon(geom.coordinates as any);
               calculatedArea = areaByUserPreference(poly);
               const pos = polylabel(geom.coordinates as any, 0.0001);
               pointCoords = [pos[0], pos[1]];
            } else if (geom.type === "MultiPolygon") {
               // For MultiPolygon, use the largest polygon
               let largestPolygon = geom.coordinates[0];
               let largestArea = 0;
               for (const polyCoords of geom.coordinates) {
                  const poly = polygon(polyCoords as any);
                  const polyArea = areaByUserPreference(poly);
                  if (polyArea > largestArea) {
                     largestArea = polyArea;
                     largestPolygon = polyCoords;
                  }
               }
               calculatedArea = largestArea;
               const pos = polylabel(largestPolygon as any, 0.0001);
               pointCoords = [pos[0], pos[1]];
            } else if (geom.type === "Point") {
               pointCoords = geom.coordinates;
               // For points, use a small default area
               calculatedArea = 1000;
            } else if (geom.type === "LineString") {
               // For lines, use midpoint and calculate approximate area from length
               const midIndex = Math.floor(geom.coordinates.length / 2);
               pointCoords = geom.coordinates[midIndex];
               // Approximate area for lines (very small)
               calculatedArea = 1000;
            }

            if (pointCoords) {
               return {
                  ...item,
                  geometry: {
                     type: "Point" as const,
                     coordinates: pointCoords
                  },
                  properties: {
                     ...item.properties,
                     polygonArea: calculatedArea
                  }
               };
            }
            return null;
         })
         .filter((item): item is NonNullable<typeof item> => item !== null);
   }, [frpMapFeatures, frpState.visualizationId]);




   useEffect(() => {
      if (!hasAccessToFeature(FRPConst.visualizations.find((a) => a.id === frpState.visualizationId)?.access ?? Feature.FRPProjectRead)) {
         frpDispatch(setVisualizationId(FRPConst.visualizations[0].id));
      }


      let map = mapitState.map.getMapPop();


      if (frpState.view === 'element') {
         frpState.selectedElementIds.forEach((id) => {
            map.setFeatureState({
               source: "FRP_Layer",
               id: id
            }, { selected: true });
         });
      }

      if (autoFit) {
         zoomed.current = undefined;
      }
      let colorReadyLayer = modLayerForVisualization(frpMapFeatures, frpState.visualizationId, frpState);
      let textReadyLayer = modLayerTextForVisualization(colorReadyLayer, frpState.visualizationId, frpState);
      if ((map.getLayer("FRP_Layer-fill") as any)?.paint?.get("fill-pattern")) { map.setPaintProperty("FRP_Layer-fill", "fill-pattern", null); }
      if ((map.getLayer("FRP_Layer-line") as any)?.paint?.get("line-pattern")) { map.setPaintProperty("FRP_Layer-line", "line-pattern", null); }
      if ((map.getLayer("FRP_Layer-point") as any)?.paint?.get("circle-pattern")) { map.setPaintProperty("FRP_Layer-point", "circle-pattern", null); }
      (map.getSource("FRP_Layer") as GeoJSONSource)?.setData(featureCollection(textReadyLayer as any[]));
      if (map.getLayer("FRP_Layer-fill")) { map.setPaintProperty("FRP_Layer-fill", getPaintPropertyKeyByMode("fill", frpState.visualizationId), getPaintPropertyByMode(frpState.visualizationId, frpState)); }
      if (map.getLayer("FRP_Layer-line")) { map.setPaintProperty("FRP_Layer-line", getPaintPropertyKeyByMode("line", frpState.visualizationId), getPaintPropertyByMode(frpState.visualizationId, frpState)); }
      if (map.getLayer("FRP_Layer-point")) { map.setPaintProperty("FRP_Layer-point", getPaintPropertyKeyByMode("circle", frpState.visualizationId), getPaintPropertyByMode(frpState.visualizationId, frpState)); }

      const usesPattern = frpState.visualizationId === "tagsV2" || frpState.visualizationId === "progress";
      if (usesPattern) {
         const opacity = getOpacityForVisualization(frpState.visualizationId, frpState);

         if (map.getLayer("FRP_Layer-fill")) { map.setPaintProperty("FRP_Layer-fill", "fill-opacity", opacity); }
         if (map.getLayer("FRP_Layer-line")) { map.setPaintProperty("FRP_Layer-line", "line-opacity", opacity); }
         if (map.getLayer("FRP_Layer-point")) { map.setPaintProperty("FRP_Layer-point", "circle-opacity", opacity); }
      } else {

         if (map.getLayer("FRP_Layer-fill")) { map.setPaintProperty("FRP_Layer-fill", "fill-opacity", 1); }
         if (map.getLayer("FRP_Layer-line")) { map.setPaintProperty("FRP_Layer-line", "line-opacity", 1); }
         if (map.getLayer("FRP_Layer-point")) { map.setPaintProperty("FRP_Layer-point", "circle-opacity", 1); }
      }

      if (map.getSource("FRP_Layer-text-points")) {
         (map.getSource("FRP_Layer-text-points") as GeoJSONSource)?.setData(featureCollection(textPointFeatures as any[]));
      }
      if (zoomed.current !== frpState.selectedProjectId && frpMapFeatures.length > 0) {

         let bounds = bbox(featureCollection(frpMapFeatures as any[]));
         map.fitBounds(bounds as unknown as any, {
            padding: { left: 100, top: 40, bottom: 40, right: Math.max(320, window.innerWidth * 0.25 + 40) },
            maxZoom: 16
         });
         zoomed.current = frpState.selectedProjectId;
      }

      return () => {
         if (map) {
            Object.values(frpState.elements).forEach((element) => {
               map.setFeatureState({
                  source: "FRP_Layer",
                  id: element.id
               }, { selected: false });
            });
         }
      }
   }, [frpState.tagOptions, frpState.statusOptions, frpMapFeatures, autoFit, frpState.visualizationId, frpState.view, frpState.filter, frpState.selectedElementIds, frpState.shownElementsIds, applicationState]);

   useEffect(() => {
      let map = mapitState.map.getMapPop();
      (map.getSource("FRP_Layer_point_source") as GeoJSONSource)?.setData(featureCollection(pointAsFeatures as any[]));
   }, [pointAsFeatures]);

   useEffect(() => {
      let map = mapitState.map.getMapPop();
      if (map.getSource("FRP_Layer-text-points")) {
         (map.getSource("FRP_Layer-text-points") as GeoJSONSource)?.setData(featureCollection(textPointFeatures as any[]));
      }
   }, [textPointFeatures]);


   function setFState(id, element: FRPElement) {
      frpDispatch(addElement(id, element));
   }




   useEffect(() => {
      const move = (e) => {
         if (e.features && e.features.length > 0) {
            const feature = e.features[0];
            const featureId = feature.id;
            if (hovered.current) {
               hovered.current.forEach((id) => {
                  map.setFeatureState({
                     source: layerName,
                     id: id
                  }, { hover: false });
               });
            }
            map.getCanvas().style.cursor = 'pointer';
            map.setFeatureState({
               source: layerName,
               id: featureId
            }, { hover: true });
            hovered.current = [featureId];
         }
      }
      const leave = () => {
         if (hovered.current) {
            hovered.current.forEach((id) => {
               map.setFeatureState({
                  source: layerName,
                  id: id
               }, { hover: false });
            });
         }
         hovered.current = null;
         map.getCanvas().style.cursor = '';
      }

      let map = mapitState.map.getMapPop();
      if (map && !map.getSource(layerName)) {
         map.addSource(layerName, {
            type: "geojson",
            data: {
               type: "FeatureCollection",
               features: frpMapFeatures as any[]
            },
            promoteId: "ueid" // Ensure the source has a unique ID for each feature
         });
         map.addSource(layerName + "_point_source", {
            type: "geojson",
            data: {
               type: "FeatureCollection",
               features: pointAsFeatures as any[]
            },
            cluster: true,
            clusterRadius: 25,
            promoteId: "ueid" // Ensure the source has a unique ID for each feature
         });
         map.addSource(layerName + "-text-points", {
            type: "geojson",
            data: {
               type: "FeatureCollection",
               features: textPointFeatures as any[]
            },
            promoteId: "ueid"
         });
         map.addLayer({
            id: layerName + "-fill",
            type: "fill",
            source: layerName,
            paint: {
               "fill-color": ['match', ["get", "status"],
                  ...frpState.statusOptions.flatMap((a) => [a.id, a.color]),
                  FRPConst.defaultColor
               ] as any,
               "fill-opacity": 1,
               "fill-outline-color": "#000"
            },
            filter: ["==", "$type", "Polygon"]
         });
         map.addLayer({
            id: layerName + "-outline",
            type: "line",
            source: layerName,
            paint: {
               "line-color": [
                  'case',
                  ["boolean", ["feature-state", "selected"], false],
                  "#f00", // Color when selected
                  ["boolean", ["feature-state", "hover"], false],
                  "#d72", // Default color
                  "#f000" // Default color
               ],
               "line-width": 4,
               "line-offset": 0
            },
            filter: ["==", "$type", "Polygon"]
         });
         map.addLayer({
            id: layerName + "-line",
            type: "line",
            source: layerName,
            paint: {
               "line-color": [
                  'case',
                  ["boolean", ["feature-state", "selected"], false],
                  "#00f", // Color when selected
                  ["boolean", ["feature-state", "hover"], false],
                  "#ff0", // Default color
                  "#f00" // Default color
               ],
               "line-width": 12,
            },
            layout: {
               "line-cap": "round",
               "line-join": "round"
            },
            filter: ["==", "$type", "LineString"]
         });
         map.addLayer({
            id: layerName + "-point",
            type: "circle",
            source: layerName,
            paint: {
               "circle-color": [
                  'case',
                  ["boolean", ["feature-state", "selected"], false],
                  "#f00", // Color when selected
                  ["boolean", ["feature-state", "hover"], false],
                  "#d72", // Default color
                  "#444" // Default color
               ],
               "circle-radius": 8,
               "circle-stroke-width": 2,
               "circle-stroke-color": "#000"
            },
            filter: ["==", "$type", "Point"]
         });
         map.addLayer({
            id: layerName + "-info",
            type: "symbol",
            source: layerName + "_point_source",
            layout: {
               "icon-image": "mit-patt-char-🔴-0-#ff0",
               "icon-anchor": "center",
               "icon-size": 1.2,
               "icon-allow-overlap": true,
               'text-allow-overlap': true,
               "icon-overlap": "always",
               'text-anchor': "center",
               'text-offset': [1, -.2],
               'text-field': '{point_count_abbreviated}',
               'text-font': ['Noto Sans Regular'],
               'text-size': 12

            },
            paint: {
               'text-halo-color': '#ffffffff',
               'text-halo-width': 2,
               'text-halo-blur': 2,
               'text-color': '#000000ff',
            }
         })
         map.addLayer({
            id: layerName + '-text',
            type: 'symbol',
            source: layerName + "-text-points",
            paint: {
               'text-halo-color': '#ffffffff',
               'text-halo-width': 3,
               'text-halo-blur': 2,
               'text-color': '#000000ff',
               'text-opacity': ["interpolate",
                  ['exponential', 0.5],
                  ['zoom'],
                  11, 0,
                  11.5, 1
               ]
            },
            layout: {
               'text-field': ['get', 'displayText'],
               'text-font': ['Noto Sans Regular'],
               'text-anchor': 'center',
               'text-max-width': 15,
               'text-allow-overlap': false,
               'text-ignore-placement': false,
               'text-size': [
                  'interpolate',
                  ['linear'],
                  ['get', 'polygonArea'],
                  0, 12,        // Small polygons: 12px
                  10000, 14,    // Medium-small: 14px
                  100000, 16,   // Medium: 16px
                  1000000, 18,  // Large: 18px
                  10000000, 20  // Very large: 20px
               ],
               'text-letter-spacing': 0.05
            }
         });

         map.on("mousemove", layerName + "-fill", move);
         map.on("mousemove", layerName + "-line", move);
         map.on("mousemove", layerName + "-point", move);
         map.on("mouseleave", layerName + "-fill", leave);
         map.on("mouseleave", layerName + "-line", leave);
         map.on("mouseleave", layerName + "-point", leave);
      }
      return () => {
         if (map && map.getSource(layerName)) {
            // map.off("mouseenter", layerName);
            map.off("mousemove", layerName + "-fill", move);
            map.off("mousemove", layerName + "-line", move);
            map.off("mousemove", layerName + "-point", move);
            map.off("mouseleave", layerName + "-fill", leave);
            map.off("mouseleave", layerName + "-line", leave);
            map.off("mouseleave", layerName + "-point", leave);


            map.removeLayer(layerName + "-fill");
            map.removeLayer(layerName + "-info");
            map.removeLayer(layerName + "-outline");
            map.removeLayer(layerName + "-line");
            map.removeLayer(layerName + "-point");
            map.removeLayer(layerName + "-text");
            map.removeSource(layerName);
            map.removeSource(layerName + "_point_source");
            if (map.getSource(layerName + "-text-points")) {
               map.removeSource(layerName + "-text-points");
            }
         }
      }
   }, []);

   useEffect(() => {
      let map = mapitState.map.getMapPop();
      let active = false;

      const handleBoxSelection = (e) => {
         if (active) {
            return false
         }
         if (e.originalEvent.shiftKey) {
            e.preventDefault();
            active = true;
            let boxRef = document.createElement("div");
            boxRef.className = "FRPBoxSelect";
            boxRef.style.left = e.originalEvent.clientX + "px";
            boxRef.style.top = e.originalEvent.clientY + "px";
            let { clientX: startX, clientY: startY } = e.originalEvent;
            document.body.appendChild(boxRef);

            const handleMouseMoveBox = (e) => {
               console.log("Dragging box selection");
               if (e.originalEvent.clientX < startX) {
                  boxRef.className = "FRPBoxSelectExcluded";
               } else {
                  boxRef.className = "FRPBoxSelect";
               }
               boxRef.style.left = Math.min(e.originalEvent.clientX, startX) + "px";
               boxRef.style.top = Math.min(e.originalEvent.clientY, startY) + "px";
               boxRef.style.width = Math.abs(e.originalEvent.clientX - startX) + "px";
               boxRef.style.height = Math.abs(e.originalEvent.clientY - startY) + "px";
            }

            const handleFinishBox = (e) => {
               map.off("mousemove", handleMouseMoveBox);
               let tl = map.unproject([Math.min(e.originalEvent.clientX, startX), Math.min(e.originalEvent.clientY, startY)]);
               let tr = map.unproject([Math.max(e.originalEvent.clientX, startX), Math.min(e.originalEvent.clientY, startY)]);
               let bl = map.unproject([Math.min(e.originalEvent.clientX, startX), Math.max(e.originalEvent.clientY, startY)]);
               let br = map.unproject([Math.max(e.originalEvent.clientX, startX), Math.max(e.originalEvent.clientY, startY)]);

               let figure = polygon([[
                  tl.toArray(),
                  tr.toArray(),
                  br.toArray(),
                  bl.toArray(),
                  tl.toArray(),
               ]])
               if (e.originalEvent.clientX < startX) {
                  // Only if completly contained
                  let ids = frpMapFeatures.filter((a) => {
                     if (!a?.properties?.id) return false;
                     if (a.geometry.type === "MultiPolygon") {
                        return (a.geometry.coordinates as any[]).every((b) => {
                           return booleanContains(figure, polygon(b));
                        });
                     }
                     return booleanContains(figure, a);
                  }).map((a) => a.properties.id)
                  if (ids.length > 0) {
                     frpDispatch(setView('element'));
                     frpDispatch(setSelectedElementIds(ids));
                  }
               } else {
                  // If inside or touches
                  let ids = frpMapFeatures.filter((a) => {
                     if (!a?.properties?.id) return false;
                     return booleanIntersects(a, figure)
                  }).map((a) => a.properties.id)
                  if (ids.length > 0) {
                     frpDispatch(setView('element'));
                     frpDispatch(setSelectedElementIds(ids));
                  }
               }
               boxRef.remove();
               active = false;

            }
            map.once("mouseup", handleFinishBox);
            map.on("mousemove", handleMouseMoveBox);
         }
      }


      function handleClick(e) {
         if (e.features && e.features.length > 0) {
            let newlySelected = e.features[0].id;
            if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) {
               if (frpState.selectedElementIds?.includes(newlySelected)) {
                  // Remove from selection
                  frpDispatch(setSelectedElementIds(frpState.selectedElementIds.filter((a) => a !== newlySelected)));
               } else {
                  // Add to selection
                  frpDispatch(setSelectedElementIds([...(frpState.selectedElementIds || []), newlySelected]));
               }
            } else {

               if (e.originalEvent.shiftKey) {

                  frpDispatch(setView('element'));
                  frpDispatch(setSelectedElementIds([newlySelected]));
                  frpDispatch(setShownElementsIds(frpState.childrenLookupTable[newlySelected] ?? [newlySelected]));
               } else {
                  frpDispatch(setView('element'));
                  frpDispatch(setSelectedElementIds([newlySelected]));
                  frpDispatch(setShownElementsIds(frpState.elements[newlySelected]?.refElementId ? frpState.childrenLookupTable[frpState.elements[newlySelected]?.refElementId] : FRPFunc.determineAllRootElements(frpState)));

               }
            }
         }
      }
      map.on("click", layerName + "-fill", handleClick);
      map.on("click", layerName + "-line", handleClick);
      map.on("click", layerName + "-point", handleClick);
      map.on("mousedown", handleBoxSelection);
      return () => {
         map.off("click", layerName + "-fill", handleClick);
         map.off("click", layerName + "-line", handleClick);
         map.off("click", layerName + "-point", handleClick);
         map.off("mousedown", handleBoxSelection);
      }
   }, [frpState, frpMapFeatures]);

   function handleDeSelect() {
      frpDispatch(setShownElementsIds([]));
   }

   const { dispatch: windowDispatch } = useWindowContext()


   //MARK: RETURN
   return (
      <>
         <AdvancedDragModal
            PosDefault={
               { left: "calc(100% - 10px)", top: "10px", transform: "translateX(-100%)", width: "max(25%, 320px)", height: "calc( 100% - 20px )" }
            }
            window={props.managedWindow} title={Localization.getText("Fremdriftsrapportering")}
            onClose={() => { windowDispatch(closeWindow(props.managedWindow.id)) }}
            subHeaderContent={
               <div style={{
                  marginBottom: "16px",
                  padding: "8px",
                  backgroundColor: "rgba(240, 240, 240, 0.4)",
                  borderRadius: "4px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  width: "100%"
               }}>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "nowrap" }}>
                     <FRPProjectSelect title={"Vælg projekt"} onSelect={(projectId => {
                        handleDeSelect();
                        frpDispatch(selectProject(projectId));
                     })} />
                     <div style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
                        <ViewButtonCollapser >
                           <ViewButtonIcon title={"Debugging"} onClick={() => {
                              console.log(frpMapFeatures);
                              console.log(frpState)
                              console.log(notification);
                           }} ><MIBIcon icon="debugging" /></ViewButtonIcon>
                           {Object.keys(frpState.filter).length > 0 &&
                              <ViewButtonFlex title={Localization.getText("Click to clear filter")} onClick={() => {
                                 frpDispatch(setFilter({}));
                              }} ><MIBIcon icon="RemoveFilter" /> {frpMapFeatures.length} of {Object.keys(frpState.elements).length}</ViewButtonFlex>}
                           <ViewButtonIcon title={Localization.getText("Import")} onClick={() => {
                              windowDispatch(openWindow(WindowId.FRPImport));
                           }}><MIBIcon icon="Import" /></ViewButtonIcon>
                           <ProtectedFeature feature={Feature.FRPProjectWrite} contentsIfNoAccess={<></>} >
                              <ViewButtonIcon title={Localization.getText("Export To Excel")} onClick={() => {
                                 windowDispatch(openWindow(WindowId.FRPExportToExcel, {
                                    value: frpState
                                 }));
                              }}><MIBIcon icon="xlsx" /></ViewButtonIcon>
                           </ProtectedFeature>
                           {frpState.selectedProject && frpState.selectedElementIds.length <= 1 ?
                              <ProtectedFeature feature={Feature.FRPProjectWrite} contentsIfNoAccess={<></>} >
                                 <ViewButtonIcon title={Localization.getText("Documents")} onClick={() => {
                                    windowDispatch(openWindow(WindowId.FRPDocuments));
                                 }}><MIBIcon icon="UploadedDocuments" /></ViewButtonIcon>
                              </ProtectedFeature>
                              : null}

                           {hasAccessToFeature(Feature.FRPProjectAdmin) &&
                              <ViewButtonIcon title={Localization.getText("Edit statuses")} onClick={() => {
                                 windowDispatch(openWindow(WindowId.FRPStatusEditor, {
                                    value: frpState.statusOptions
                                 }));
                              }} >
                                 <MIBIcon icon="Status" />
                              </ViewButtonIcon>}
                           {hasAccessToFeature(Feature.FRPProjectAdmin) &&
                              <ViewButtonIcon title={Localization.getText("Edit tags")} onClick={() => {
                                 windowDispatch(openWindow(WindowId.FRPTagEditor, {
                                    value: frpState.tagOptions
                                 }));
                              }} ><MIBIcon icon="Tags" /></ViewButtonIcon>
                           }
                           <ViewButtonIcon title={Localization.getText("Search")} onClick={() => {
                              windowDispatch(openWindow(WindowId.FRPSearch));
                           }}><MIBIcon icon="Search" /></ViewButtonIcon>
                           <ViewButtonIcon title={Localization.getText("Plot")} onClick={(() => {
                              let map = mapitState.map.getMapPop()
                              let handle = new MitLayerHandleMaplibre()
                              let colorReadyLayer = modLayerForVisualization(frpMapFeatures, frpState.visualizationId, frpState);
                              let textReadyLayer = modLayerTextForVisualization(colorReadyLayer, frpState.visualizationId, frpState);
                              handle.addSource(layerName, {
                                 type: "geojson",
                                 data: featureCollection(textReadyLayer as any[]),
                                 promoteId: "ueid"
                              })
                              map.getStyle().layers?.forEach((layer) => {
                                 if (layer.id.startsWith(layerName)) {
                                    handle.addLayer(layer);
                                 }
                              });
                              mapitStateDispatch(SetPrintLayers([{ type: "MitLayer", handle }]))
                              windowDispatch(openWindow(WindowId.PlotScreen))

                           })}><MIBIcon icon="Plot" /></ViewButtonIcon>
                           <ViewButtonGroup >
                              <ViewButtonIcon title={Localization.getText("Fitscreen")} onClick={() => {
                                 let bounds = bbox(featureCollection(frpMapFeatures as any[]));
                                 mapitState.map.getMapPop().fitBounds(bounds as unknown as any, {
                                    padding: { left: 100, top: 40, bottom: 40, right: Math.max(320, window.innerWidth * 0.25 + 40) },
                                    maxZoom: 16
                                 });
                              }} ><MIBIcon icon="AdjustMap" /></ViewButtonIcon>
                              <ViewButtonCheck checked={autoFit} onCheck={() => setAutoFit((e) => !e)} title={Localization.getText("Auto fitscreen")} onClick={() => {

                              }}>Auto</ViewButtonCheck>
                           </ViewButtonGroup>
                        </ViewButtonCollapser>
                     </div>
                  </div>
                  <div style={{ margin: "0" }}>
                     <FRPSearchBar callbackOnSelect={(element: any) => {
                        FRPFunc.goToElement(frpState, frpDispatch, element.id)
                     }} />
                  </div>
               </div>
            }
         >
            <FRPAnotations
               hovered={hovered}
               ondrop={() => { }}
               msgDispatch={(msg: FRPMessage) => {
                  frpDispatch(addElement(msg.id, msg));
               }}
               lState={frpState.messages}
               frpMapFeatures={frpMapFeatures}
               fUpdate={setFState}
            />
         </AdvancedDragModal>

      </>
   )
}

export function FRPAnotations(props: {
   hovered: React.RefObject<string[] | null>,
   msgDispatch: (msg: FRPMessage) => void,
   lState: any[],
   frpMapFeatures: any[],
   fUpdate: (id, element) => void,
   ondrop: (e: React.DragEvent<HTMLDivElement>) => void,
}) {

   const { state: mapitState } = useContext(MapitStateContext);
   const { state: frpState } = useContext(FRPContext);

   function hoverElement(elementID: string | string[] | null) {
      if (props.hovered.current) {
         props.hovered.current.forEach((id) => {
            mapitState.map.getMapPop().setFeatureState({
               source: "FRP_Layer",
               id: id
            }, { hover: false });
         });
      }
      if (elementID) {
         if (Array.isArray(elementID)) {
            elementID.forEach((id) => {
               mapitState.map.getMapPop().setFeatureState({
                  source: "FRP_Layer",
                  id: id
               }, { hover: true });
            });
            (props.hovered.current as any) = elementID;
            return;
         } else {
            mapitState.map.getMapPop().setFeatureState({
               source: "FRP_Layer",
               id: elementID
            }, { hover: true });
         }
         (props.hovered.current as any) = [elementID];
         return
      }
      (props.hovered.current as any) = null;
   }

   return (
      <ADModalBodyNoScroll templateRows={"auto"}>
         <div className="FRP_dialogCon">
            <div style={{ padding: "0px 8px" }}>
               {frpState.view === 'element' ?
                  <FRPElementEditor
                     hoverElement={hoverElement}
                     exitElement={() => {
                        hoverElement(null);
                     }}
                  /> :
                  <FRPProjektView
                     onHover={hoverElement}
                  />
               }
            </div>
            <FRPContentView
               elementID={frpState.selectedElementIds?.length === 1 ? frpState.selectedElementIds[0] : undefined}
               hoverElement={(elementID) => {
                  hoverElement(elementID ?? null);
               }}
               exitElement={() => {
                  hoverElement(null);
               }}
            />
         </div>
      </ADModalBodyNoScroll>
   )
}

// ---------------- Visualization related functions ----------------

function getPaintPropertyByMode(visualization: (typeof FRPConst.visualizations)[number]["id"], frpState?: FRPState) {
   if (!frpState || !frpState.elements) {
      return ['match', ["get", "status"],
         "behind", "rgba(255, 0, 0, 0.6)",
         "ontrack", "rgba(0, 255, 0, 0.6)",
         "completed", "rgba(0, 85, 0, 0.6)",
         "cancelled", "rgba(112, 0, 0, 0.6)",
         FRPConst.defaultColor
      ];
   }
   switch (visualization) {
      case "lastChanged": {
         return ['interpolate-hcl', ['linear'], ["get", "color"],
            0, FRPConst.lastChangedColors.oldest,
            // 0.5, "#ff0",
            1, FRPConst.lastChangedColors.newest
         ];
      }
      case "status":
      case "progress":
      case "processStatus":
      case "tagsV2": {
         return ["get", "color"]
      }
   }
}

function getPaintPropertyKeyByMode(type: "line" | "fill" | "circle", visualization: (typeof FRPConst.visualizations)[number]["id"]) {
   return ({
      "tagsV2": {
         "line": "line-pattern",
         "fill": "fill-pattern",
         "point": "circle-pattern",
      },
      "progress": {
         "line": "line-pattern",
         "fill": "fill-pattern",
         "point": "circle-pattern",
      }
   }?.[visualization]?.[type] ?? (type + "-color"));
}

function getOpacityForVisualization(visualization: (typeof FRPConst.visualizations)[number]["id"], frpState: FRPState): number {
   // Helper function to extract opacity from rgba/rgb color string
   const extractOpacity = (colorString: string): number | null => {
      if (!colorString) return null;
      // Match rgba(r, g, b, a) or rgb(r, g, b)
      const rgbaMatch = colorString.match(/rgba?\([\d\s,]+,\s*([\d.]+)\)/);
      if (rgbaMatch) {
         return parseFloat(rgbaMatch[1]);
      }
      // Try chroma for hex colors or other formats
      try {
         const chromaColor = chroma(colorString);
         return chromaColor.alpha();
      } catch {
         return null;
      }
   };

   if (visualization === "tagsV2") {
      // Fixed opacity for tagsV2 visualization
      return 0.6;
   } else if (visualization === "progress") {
      // For progress, calculate average opacity from all statusOptions
      if (frpState.statusOptions.length > 0) {
         const opacities = frpState.statusOptions
            .map(status => extractOpacity(status.color))
            .filter(op => op !== null) as number[];

         if (opacities.length > 0) {
            return opacities.reduce((sum, op) => sum + op, 0) / opacities.length;
         }
      }
      // Fallback: extract from defaultColor
      const defaultOpacity = extractOpacity(FRPConst.defaultColor);
      return defaultOpacity !== null ? defaultOpacity : 1;
   }

   // Default opacity for non-pattern visualizations
   return 1;
}

function modLayerForVisualization(frpMapFeatures: { properties: FRPElement }[], visualization: (typeof FRPConst.visualizations)[number]["id"], frpState: FRPState) {
   switch (visualization) {
      case "lastChanged": {
         let dateToNumber = (date: Date | string | undefined, ifUnd: number) => {
            if (!date) {
               return ifUnd;
            }
            if (typeof date === "string") {
               date = new Date(date);
            }
            return date.getTime();
         }

         const now = new Date();
         const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days ago
         const maxDate = now.getTime();
         const minDate = oneWeekAgo.getTime();

         return frpMapFeatures.map((item) => {
            let descendants = FRPFunc.getAllDescendants(item.properties.id, frpState.childrenLookupTable);

            let descendantElements = descendants.map(id => frpState.elements[id]).filter(Boolean);

            const itemDate = dateToNumber(item.properties.lastChangedTimestamp, minDate);
            const clampedDate = Math.max(minDate, Math.min(maxDate, itemDate));
            let lastChanged = (clampedDate - minDate) / (maxDate - minDate);

            if (descendantElements.length > 0) {
               const descendantColors = descendantElements.map(descendant => {
                  const descDate = dateToNumber(descendant.lastChangedTimestamp, minDate);
                  const descClampedDate = Math.max(minDate, Math.min(maxDate, descDate));
                  return (descClampedDate - minDate) / (maxDate - minDate);
               });

               const maxDescendantColor = Math.max(...descendantColors);

               lastChanged = Math.max(lastChanged, maxDescendantColor);
            }

            return {
               ...item,
               properties: {
                  ...item.properties,
                  color: lastChanged
               }
            };
         });
      }
      case "tagsV2": {
         // TODO: Implement a more sophisticated color mapping for tags evt. on FRPProject settings

         let tagToColor = frpState.tagOptions.reduce((acc, tag) => {
            acc[tag.id] = tag.color;
            return acc;
         }, {});

         // Helper function to convert rgba to hex for pattern names (pattern names are split by "-")
         const rgbaToHexForPattern = (rgbaColor: string): string => {
            try {
               // If it's already hex, return it
               if (rgbaColor.startsWith("#")) {
                  return rgbaColor;
               }
               // Parse rgba string like "rgba(255, 107, 53, 0.5)"
               const match = rgbaColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
               if (match) {
                  const r = parseInt(match[1]).toString(16).padStart(2, '0');
                  const g = parseInt(match[2]).toString(16).padStart(2, '0');
                  const b = parseInt(match[3]).toString(16).padStart(2, '0');
                  return `#${r}${g}${b}`;
               }
               // Fallback: try chroma
               return chroma(rgbaColor).hex();
            } catch {
               return "#EEEEEE"; // Default gray (hex version of FRPConst.defaultColor)
            }
         };

         return frpMapFeatures.map((item) => {
            let color = (item.properties.tags || []).filter((tag) => tagToColor[tag] !== undefined).sort()?.map((tag) => {
               const rgbaColor = tagToColor[tag] ?? FRPConst.defaultColor;
               return rgbaToHexForPattern(rgbaColor);
            });
            const defaultColor = rgbaToHexForPattern(FRPConst.defaultColor);
            return returnColorPackage(item, "mit-patt-diag-" + (color.join("-") || defaultColor));
         });
      }
      case "progress": {
         // Helper function to convert rgba to hex for pattern names
         const rgbaToHexForPattern = (rgbaColor: string): string => {
            try {
               if (rgbaColor.startsWith("#")) {
                  return rgbaColor;
               }
               const match = rgbaColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
               if (match) {
                  const r = parseInt(match[1]).toString(16).padStart(2, '0');
                  const g = parseInt(match[2]).toString(16).padStart(2, '0');
                  const b = parseInt(match[3]).toString(16).padStart(2, '0');
                  return `#${r}${g}${b}`;
               }
               return chroma(rgbaColor).hex();
            } catch {
               return "#EEEEEE";
            }
         };

         const stripeColor = rgbaToHexForPattern(FRPConst.progressColors.stripe);
         const backgroundColor = rgbaToHexForPattern(FRPConst.progressColors.background);

         return frpMapFeatures.map((item) => {
            const progress = FRPFunc.getElementProgressRecursive(item.properties.id, 0, frpState);
            const progressColor = progress.count > 0 ? progress.status : 0;
            return returnColorPackage(item, "mit-patt-pdiag-" + progressColor + "-" + stripeColor + "-" + backgroundColor);
         });
      }
      case "processStatus": {
         let colormap = {
            "bagTidsplan": FRPConst.processStatusColors.bagTidsplan,
            "manglerTidsplan": FRPConst.processStatusColors.manglerTidsplan,
            "folgtTidsplan": FRPConst.processStatusColors.folgtTidsplan,
            "ingenStatus": FRPConst.processStatusColors.ingenStatus
         }

         return frpMapFeatures.map((item) => {
            let currentDate = new Date();

            let descendants = FRPFunc.getAllDescendants(item.properties.id, frpState.childrenLookupTable);
            let descendantElements = descendants.map(id => frpState.elements[id]).filter(Boolean);



            let processedDescendants = modLayerForVisualization(
               descendantElements.map(el => {
                  let expectedStartDate = FRPFunc.determineParentAttribute(el, frpState, "expectedStartDate")?.value;
                  let expectedEndDate = FRPFunc.determineParentAttribute(el, frpState, "expectedEndDate")?.value;
                  let status = FRPFunc.determineParentAttribute(el, frpState, "status")?.value;

                  return {
                     properties: {
                        ...el,
                        expectedStartDate: expectedStartDate,
                        expectedEndDate: expectedEndDate,
                        status: status || "",
                     }
                  };
               }),
               visualization,
               frpState
            );
            let descendantsHaveRed = processedDescendants.some(descendant =>
               descendant.properties.color === colormap["bagTidsplan"]
            );

            if (descendantsHaveRed) {
               return returnColorPackage(item, "bagTidsplan", colormap);
            }

            let descendantsHaveYellow = processedDescendants.some(descendant =>
               descendant.properties.color === colormap["manglerTidsplan"]
            );

            if (descendantsHaveYellow) {
               return returnColorPackage(item, "manglerTidsplan", colormap);
            }



            let startDate = item.properties.expectedStartDate ?? new Date("2030-01-01").toISOString();
            let endDate = item.properties.expectedEndDate ?? new Date("2030-01-01");

            let status = item.properties.status;
            let processStatus = frpState.statusOptions.find((a) => a.id === status)?.status;

            let baseColor = "manglerTidsplan";

            if (processStatus && processStatus >= 100) {
               baseColor = "folgtTidsplan";
            }
            else if (processStatus && processStatus > 0 && processStatus < 100) {
               if (item.properties.expectedStartDate === undefined) {
                  if (item.properties.expectedEndDate && new Date(item.properties.expectedEndDate).toISOString() < currentDate.toISOString()) {
                     baseColor = "bagTidsplan";
                  } else {
                     baseColor = "folgtTidsplan";
                  }
               } else if (currentDate.toISOString() >= new Date(startDate).toISOString() && currentDate.toISOString() <= new Date(endDate).toISOString()) {
                  baseColor = "folgtTidsplan";
               } else if (currentDate.toISOString() < new Date(startDate).toISOString()) {
                  baseColor = "folgtTidsplan";
               } else {
                  baseColor = "bagTidsplan";
               }
            }
            else if (processStatus === 0) {
               if (item.properties.expectedStartDate === undefined) {
                  baseColor = "manglerTidsplan";
               } else if (currentDate.toISOString() > new Date(startDate).toISOString()) {
                  baseColor = "bagTidsplan";
               } else {
                  baseColor = "folgtTidsplan";
               }
            }
            else {
               baseColor = "folgtTidsplan";
            }


            return returnColorPackage(item, baseColor, colormap);
         });
      }
      case "status": {
         return frpMapFeatures.map((item: any) => {
            const elementId = item.properties.id;
            const element = frpState.elements[elementId];
            if (!element) {
               return returnColorPackage(item, FRPConst.defaultColor);
            }
            const { status: inferredStatus } = FRPFunc.determineNestedChildrenStatusWithSource(element, frpState);
            const color = inferredStatus ? (frpState.statusOptions.find(opt => opt.id === inferredStatus)?.color || FRPConst.defaultColor) : FRPConst.defaultColor;

            return returnColorPackage(item, color);

         });
      }
      default:
         return frpMapFeatures
   }

   function returnColorPackage(item: any, colorKey: string, colormap?: { [key: string]: string }) {
      if (colormap) {
         const colorWithAlpha = colormap[colorKey] || colorKey;
         return {
            ...item,
            properties: {
               ...item.properties,
               color: colorWithAlpha
            }
         };
      } else {
         return {
            ...item,
            properties: {
               ...item.properties,
               color: colorKey
            }
         };
      }
   }

}



function modLayerTextForVisualization(frpMapFeatures: { properties: FRPElement }[], visualization: (typeof FRPConst.visualizations)[number]["id"], frpState: FRPState) {
   switch (visualization) {
      case "tagsV2":
         let tagToText = Object.fromEntries(frpState.tagOptions.map(tag => [tag.id, tag.val]));
         return frpMapFeatures.map(f => ({
            ...f,
            properties: {
               ...f.properties,
               displayText: (f.properties.tags || []).map((a) => tagToText[a]).join('\n') || ''
            }
         }));
      case "status":
         return frpMapFeatures.map(f => {
            const elementId = f.properties.id;
            const element = frpState.elements[elementId];
            if (!element) {
               return {
                  ...f,
                  properties: {
                     ...f.properties,
                     displayText: Localization.getText("No status")
                  }
               };
            }
            const { status: inferredStatus } = FRPFunc.determineNestedChildrenStatusWithSource(element, frpState);
            const statusText = inferredStatus
               ? (frpState.statusOptions.find(a => a.id === inferredStatus)?.val || inferredStatus)
               : Localization.getText("No status");

            return {
               ...f,
               properties: {
                  ...f.properties,
                  displayText: statusText
               }
            };
         });
      case "progress":
         return frpMapFeatures.map(f => {
            const statusResult = FRPFunc.getElementProgressRecursive(f.properties.id, 0, frpState);
            const statusText = statusResult.count > 0 ?
               `${statusResult.status}%` :
               'No status';

            return {
               ...f,
               properties: {
                  ...f.properties,
                  displayText: statusText
               }
            };
         });
      case "lastChanged":
         return frpMapFeatures.map(f => {
            const elementId = f.properties.id;
            const element = frpState.elements[elementId];
            let displayText = '';
            if (element?.lastChangedTimestamp) {
               const date = new Date(element.lastChangedTimestamp);
               // Format as short date and time
               displayText = date.toLocaleString('da-DK', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
               });
            }
            return {
               ...f,
               properties: {
                  ...f.properties,
                  displayText: displayText
               }
            };
         });
      case "processStatus":
         return frpMapFeatures.map(f => {
            const elementId = f.properties.id;
            const element = frpState.elements[elementId];
            const displayName = element?.name || elementId || '';
            return {
               ...f,
               properties: {
                  ...f.properties,
                  displayText: displayName.toUpperCase()
               }
            };
         });
      default:
         return frpMapFeatures;
   }
}
